import _ from "lodash";
import moment from "moment";
import i18n from "src/i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getBillsPaymentData, getPaymentReceiptAsync } from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { getMypageTitle } from "src/utils/app-Info";
import { getUserDevice, numberToStringWithComma, ViewYmdFormat } from "src/utils/common-util";

/**
 * 마이페이지 메인 > 신청/계약 내역 > 상세 > 결제 내역 / 연체 내역  > 상세
 */
const ContractPaymentDetail = () => {
  const { t } = useTranslation(["contract", "common"]);
  const navigate = usePartnerNavigate();

  const device = getUserDevice();

  // path variable 계약 id, 청구 id
  const { contractId, billId } = useParams();
  // 거래내역 확인서 url
  const { executeAsync: getPaymentReceipt } = useApiOperation(getPaymentReceiptAsync);
  //결제 내역 및 취소네역
  const { executeAsync: fetchPaymentList } = useApiOperation(getBillsPaymentData);

  const location = useLocation();

  const [lastPayment, setLastPayment] = useState<any>({});
  const [payments, setPayments] = useState<any>([]);

  const paymentState: any | null = useMemo(() => {
    // let paymentView: any = payment.payment ? payment.payment : payment;
    if (location.state) {
      return location.state as any;
    }
    return null;
  }, [location]);

  const findS2Payments = useCallback(
    async (billId: string, pgcode: string) => {
      const response = await fetchPaymentList({ billId, pgcode });
      if (response.data.data.content && response.data.data.content.length > 0) {
        let sort = _.sortBy(response.data.data.content, "transactionDate").reverse();
        setLastPayment(sort[0]);
        setPayments(sort);
      }
    },
    [fetchPaymentList],
  );

  // 거래내역 확인서
  const fetchPaymentReciptUrl = async (payment: any) => {
    const { data } = await getPaymentReceipt({ id: String(payment.paymentId) });
    const url = data.data.receipt_url;

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "openInAppBrowser",
          data: {
            event: url,
          },
        }),
      );
    } else {
      window.open(url);
      // window.location.href = url;
    }
  };

  useEffect(() => {
    findS2Payments(paymentState.billId, paymentState.pgcode);
  }, [findS2Payments, paymentState]);

  return (
    <>
      <MetaTag title={getMypageTitle()} />
      <Header
        headerType="CLOSE"
        title={t("paymentHistory.detailTitle")}
        onClickCloseButton={() => {
          console.log("onClickCloseButton");
          navigate(-1);
        }}
      />

      <div className="payment-page">
        <article>
          <section className="payment-main">
            <h1>{/* TODO: paymentLabel 로 표기하는게 맞는지 ? */}</h1>
            <div className="contents-card mb30">
              <div className="contents-card__title-area">
                <p>{moment(lastPayment?.transactionDate).locale(i18n.language?.startsWith("en") ? "en" : "ko").format(ViewYmdFormat.YYYY_MM_DD_WEEK)}</p>

                <div className={`base-chip ${lastPayment?.paymentCancel && "color-gray"}`}>
                  <span>{!lastPayment?.paymentCancel ? t("paymentHistory.statusCompleted") : t("paymentHistory.statusCancelled")}</span>
                </div>
              </div>
              <div className="contents-card__info-area detail">
                <p className="">
                  {lastPayment?.productName}
                  {/* {payment?.paymentLabel} */}
                </p>
                <div className="flex-center">
                  <p>
                    <span className="value">
                      {numberToStringWithComma(
                        Number(!lastPayment?.paymentCancel ? lastPayment?.totalAmount : lastPayment.paymentCancel.cancelAmount || 0),
                      )}
                    </span>{" "}
                    {t("common:currencyUnit")}
                  </p>
                </div>
              </div>
            </div>
          </section>
          {payments.map((payment: any, index: number) => {
            return (
              <section className="base-section-layout" key={index}>
                <div className="base-section-layout__container">
                  <div className="base-section-layout__wrap">
                    <div className="base-section-layout__title-wrap">
                      <h2>{t("paymentHistory.paymentInfo")}</h2>
                    </div>
                    {/* TODO: 결제번호 */}
                    <div className="base-section-layout-line">
                      <p className="base-section-layout-index text-primary3">{t("paymentHistory.paymentNumber")}</p>
                      <p className="base-section-layout-value">{payment?.tid || "-"}</p>
                    </div>
                    {/* TODO: 결제취소 금액 */}
                    {/* {payment?.paymentStatus === "PAYMENT_CANCEL" && (
                  <div className="base-section-layout-line">
                    <p className="base-section-layout-index text-primary3">취소금액</p>
                    <p className="base-section-layout-value">- 0원</p>
                  </div>
                )} */}
                    <div className="base-section-layout-line">
                      <p className="base-section-layout-index text-primary3">{t("paymentHistory.paymentAmount")}</p>
                      <p className="base-section-layout-value">{numberToStringWithComma(Number(payment?.totalAmount || 0))} {t("common:currencyUnit")}</p>
                    </div>
                    <div className="base-section-layout-line">
                      <p className="base-section-layout-index text-primary3">{t("paymentHistory.paymentDateTime")}</p>
                      <p className="base-section-layout-value">{moment(payment?.transactionDate).format(ViewYmdFormat.FULL)}</p>
                    </div>
                    {/* 취소일시 */}
                    {payment?.paymentCancel && (
                      <div className="base-section-layout-line">
                        <p className="base-section-layout-index text-primary3">{t("paymentHistory.cancelDateTime")}</p>
                        <p className="base-section-layout-value">
                          {payment?.paymentCancel ? moment(payment?.paymentCancel?.cancelDate).format(ViewYmdFormat.FULL) : "-"}
                        </p>
                      </div>
                    )}
                  </div>
                  <button
                    className="base-btn"
                    onClick={() => {
                      fetchPaymentReciptUrl(payment);
                    }}
                  >
                    {t("paymentHistory.receiptButton")}
                  </button>
                </div>
              </section>
            );
          })}

          <section className="px24">
            <p className="mt20 font14 keep-all text-primary4">
              {device === "android"
                ? t("paymentHistory.paymentGatewayNoticeAndroid")
                : device === "ios"
                ? t("paymentHistory.paymentGatewayNoticeIos")
                : ""}
            </p>
          </section>
        </article>
      </div>
    </>
  );
};
export default ContractPaymentDetail;
